import { type ColumnKey, DEFAULT_COLUMN_WIDTHS } from './useColumnPreferences';

/**
 * How the inventory and evaluation tables are sorted and filtered, as search
 * params, so a link opens on the view its sender was looking at. Every param is
 * left out at its default, so an untouched view keeps a bare URL.
 *
 *   sort=newest          a "Sort by" choice (alphabetical, the default, is left out)
 *   sort=category:desc   a click on a column heading, and which way
 *   unpriced=true        only items with no RCV
 *   unapproved=true      only items not yet approved
 *   variance=true        only items whose comparables' prices vary widely
 *   min=100              only items with an RCV of at least this
 *
 * Pure, so it is unit tested in Vitest's node environment.
 */

/** A column a table can be sorted by: any data column, or the always-shown name. */
export type SortColumn = ColumnKey | 'name';

export type SortDirection = 'asc' | 'desc';

export type ColumnSort = { column: SortColumn; direction: SortDirection };

/** The distribution is a chart of the comparables' prices, with no one value to order by. */
export const isSortableColumn = (column: SortColumn): boolean => column !== 'distribution';

/** The next sort after a click on `column`'s heading: ascending first, then flipping each click. */
export const nextColumnSort = (current: ColumnSort | null, column: SortColumn): ColumnSort =>
  current?.column === column
    ? { column, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { column, direction: 'asc' };

export const SORT_OPTIONS = ['alphabetical', 'newest', 'oldest', 'itemNumber', 'price', 'category'] as const;

export type SortOption = (typeof SORT_OPTIONS)[number];

export const DEFAULT_SORT_OPTION: SortOption = 'alphabetical';

export type TableViewSearch = {
  sort?: string;
  unpriced?: true;
  unapproved?: true;
  variance?: true;
  min?: number;
};

/** The table's order: a "Sort by" choice, or a column heading's sort, which wins. */
export type TableSort = { option: SortOption; column: ColumnSort | null };

// Every data column (DEFAULT_COLUMN_WIDTHS has one entry per column), and the name.
const SORT_COLUMNS: SortColumn[] = ['name', ...(Object.keys(DEFAULT_COLUMN_WIDTHS) as ColumnKey[])];

const isFlag = (value: unknown) => value === true || value === 'true' || value === 1 || value === '1';

/**
 * The params as the route keeps them: anything unrecognised dropped, so a
 * hand-edited or stale link falls back to the default rather than breaking.
 */
export const validateTableViewSearch = (search: Record<string, unknown>): TableViewSearch => {
  const sort = typeof search.sort === 'string' ? search.sort : undefined;
  const min = typeof search.min === 'number' ? search.min : Number.parseFloat(String(search.min ?? ''));
  return {
    sort: formatTableSort(parseTableSort(sort)),
    unpriced: isFlag(search.unpriced) ? true : undefined,
    unapproved: isFlag(search.unapproved) ? true : undefined,
    variance: isFlag(search.variance) ? true : undefined,
    min: Number.isFinite(min) && min > 0 ? min : undefined,
  };
};

const formatColumn = (column: ColumnSort | null) => (column ? `${column.column}:${column.direction}` : '');

/** The order `sort` asks for; the default for anything it does not recognise. */
export const parseTableSort = (sort: string | undefined): TableSort => {
  const [name, direction] = (sort ?? '').split(':');
  if (direction === 'asc' || direction === 'desc') {
    const column = SORT_COLUMNS.find((candidate) => candidate === name);
    if (column && isSortableColumn(column)) return { option: DEFAULT_SORT_OPTION, column: { column, direction } };
  }
  const option = SORT_OPTIONS.find((candidate) => candidate === name && direction === undefined);
  return { option: option ?? DEFAULT_SORT_OPTION, column: null };
};

/** `sort` for the URL; undefined (left out) for the default order. */
export const formatTableSort = (sort: TableSort): string | undefined => {
  if (sort.column) return formatColumn(sort.column);
  return sort.option === DEFAULT_SORT_OPTION ? undefined : sort.option;
};
